import React, { useEffect, useRef, useState } from 'react';
import { useEditorState } from '@tiptap/react';
import { Box, Button, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import { Close, KeyboardArrowDown, KeyboardArrowUp, FindReplace } from '@mui/icons-material';

const inputSx = {
  flex: '1 1 160px',
  minWidth: 120,
  height: 32,
  px: 1.25,
  fontSize: 14,
  borderRadius: '8px',
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  '&.Mui-focused': { borderColor: 'var(--q-accent, #4F3BD6)', boxShadow: '0 0 0 3px rgba(79,59,214,0.12)' },
};

/**
 * Find & replace bar (Mod-F). Drives the SearchReplace extension.
 * @param {{editor: import('@tiptap/core').Editor, open: boolean, onClose: () => void, readOnly?: boolean, focusToken?: number}} props - Props.
 * @returns {JSX.Element|null}
 */
const FindReplaceBar = ({ editor, open, onClose, readOnly = false, focusToken = 0 }) => {
  const [term, setTerm] = useState('');
  const [replacement, setReplacement] = useState('');
  const [showReplace, setShowReplace] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const inputRef = useRef(null);

  const { count, index } = useEditorState({
    editor,
    selector: ({ editor: ed }) => ({
      count: ed?.storage?.searchReplace?.results?.length || 0,
      index: ed?.storage?.searchReplace?.index || 0,
    }),
  }) || { count: 0, index: 0 };

  useEffect(() => {
    if (!open) return;
    const { from, to, empty } = editor.state.selection;
    if (!empty && to - from < 120) {
      const selected = editor.state.doc.textBetween(from, to, ' ');
      if (selected && !selected.includes('\n')) {
        setTerm(selected);
        editor.commands.setSearchTerm(selected);
      }
    }
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }, [open, focusToken, editor]);

  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (wasOpenRef.current && !open && editor && !editor.isDestroyed) editor.commands.clearSearch();
    wasOpenRef.current = open;
  }, [open, editor]);

  if (!open) return null;

  /** Close the bar and return focus to the document. */
  const close = () => {
    editor.commands.clearSearch();
    onClose();
    editor.commands.focus();
  };

  return (
    <Box
      role="search"
      aria-label="Find and replace"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 1,
        mb: 1,
        borderRadius: 'var(--q-radius-md, 10px)',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'var(--q-surface, #FFFFFF)',
        position: 'sticky',
        top: 'calc(var(--q-editor-sticky-top, 0px) + 52px)',
        zIndex: 5,
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          close();
        }
      }}
    >
      <InputBase
        inputRef={inputRef}
        placeholder="Find in document"
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          editor.commands.setSearchTerm(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) editor.commands.previousSearchResult();
            else editor.commands.nextSearchResult();
          }
        }}
        inputProps={{ 'aria-label': 'Find' }}
        sx={inputSx}
      />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56, textAlign: 'center' }} aria-live="polite">
        {term ? (count ? `${index + 1} of ${count}` : 'No results') : ''}
      </Typography>
      <Tooltip title="Previous (Shift+Enter)">
        <span>
          <IconButton size="small" aria-label="Previous match" disabled={!count} onClick={() => editor.commands.previousSearchResult()}>
            <KeyboardArrowUp fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Next (Enter)">
        <span>
          <IconButton size="small" aria-label="Next match" disabled={!count} onClick={() => editor.commands.nextSearchResult()}>
            <KeyboardArrowDown fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Match case">
        <Button
          size="small"
          aria-pressed={caseSensitive}
          onClick={() => {
            setCaseSensitive((v) => !v);
            editor.commands.setSearchCaseSensitive(!caseSensitive);
          }}
          sx={{ minWidth: 36, height: 32, px: 1, fontWeight: 600, color: caseSensitive ? 'var(--q-accent-strong, #2F2096)' : 'text.secondary', bgcolor: caseSensitive ? 'var(--q-accent-soft, #ECE8FC)' : 'transparent' }}
        >
          Aa
        </Button>
      </Tooltip>
      {!readOnly && (
        <Tooltip title="Replace">
          <IconButton size="small" aria-label="Toggle replace" aria-pressed={showReplace} onClick={() => setShowReplace((v) => !v)}>
            <FindReplace fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <IconButton size="small" aria-label="Close find" onClick={close} sx={{ ml: 'auto' }}>
        <Close fontSize="small" />
      </IconButton>
      {showReplace && !readOnly && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, width: '100%' }}>
          <InputBase
            placeholder="Replace with"
            value={replacement}
            onChange={(e) => {
              setReplacement(e.target.value);
              editor.commands.setReplaceTerm(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                editor.commands.replaceCurrentResult();
              }
            }}
            inputProps={{ 'aria-label': 'Replace with' }}
            sx={inputSx}
          />
          <Button size="small" variant="outlined" disabled={!count} onClick={() => editor.commands.replaceCurrentResult()}>
            Replace
          </Button>
          <Button size="small" variant="outlined" disabled={!count} onClick={() => editor.commands.replaceAllResults()}>
            Replace all
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FindReplaceBar;
